import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import apiClient from './apiClient';

// Characters not allowed in folder/file names on Windows, macOS and/or Linux
const UNSAFE_FOLDER_CHARS_REGEX = /[\\/:*?"<>|]/g;

const sanitizeForFolderName = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .trim()
    .replace(UNSAFE_FOLDER_CHARS_REGEX, '')
    .replace(/\s+/g, '');
};

// e.g. "John Doe" -> "JD"; numeric words are kept in full, e.g. "20013 Elling Bjerke" -> "20013EB"
export const getPatientInitials = (patientName) => {
  if (!patientName || typeof patientName !== 'string') return '';
  return patientName
    .trim()
    .split(/\s+/)
    .map((word) => (/^\d+$/.test(word) ? word : word.charAt(0)))
    .join('')
    .toUpperCase();
};

// Builds e.g. "AA_13075_843743", omitting initials and/or ref when unavailable
export const buildOrderFolderName = (initials, ref, orderNumber) => {
  const parts = [initials, ref, orderNumber]
    .map(sanitizeForFolderName)
    .filter(Boolean);
  return parts.length > 0 ? parts.join('_') : `Order_${orderNumber || Date.now()}`;
};

export const downloadAndZipFiles = async (fileEntries, setIsDownloading, setProgress, setStatus) => {
  if (!fileEntries || !Array.isArray(fileEntries) || fileEntries.length === 0) {
    setStatus('No files to download');
    return;
  }

  setIsDownloading(true);
  setProgress(0);
  setStatus('Preparing to download files...');

  try {
    // Prepare the request payload
    const files = fileEntries.map(({ fileLink, initials, ref, orderNumber }) => ({
      fileLink,
      filename: fileLink.split('/').pop(), // Extract filename from the link
      initials: initials || '',
      ref: ref || '',
      orderNumber: orderNumber || '',
    }));

    // If every file belongs to the same order, name the zip after that
    // order's folder instead of the generic "cadmus_files_..." name.
    const uniqueOrderNumbers = new Set(fileEntries.map((entry) => entry.orderNumber));
    const zipFilename = uniqueOrderNumbers.size === 1
      ? `${buildOrderFolderName(fileEntries[0].initials, fileEntries[0].ref, fileEntries[0].orderNumber)}.zip`
      : `cadmus_files_${Date.now()}.zip`;

    // Send request to the backend to fetch files as base64
    const response = await apiClient.post('/files/bulk-download-stream', {
      organizationId: parseInt(localStorage.getItem('organization_id')),
      files,
    }, {
      responseType: 'json',
    });

    const { files: downloadedFiles } = response.data;

    if (!downloadedFiles || downloadedFiles.length === 0) {
      setStatus('No files were downloaded');
      setIsDownloading(false);
      return;
    }

    // Create a new ZIP instance
    const zip = new JSZip();

    // Group files by order (initials_ref_orderNumber) and add to ZIP with subfolders
    downloadedFiles.forEach((file, index) => {
      if (file.data && file.orderNumber) {
        const folderName = buildOrderFolderName(
          file.fileInfo?.initials,
          file.fileInfo?.ref,
          file.fileInfo?.orderNumber || file.orderNumber
        );
        const orderFolder = zip.folder(folderName);
        // Add the file to the order-specific folder
        orderFolder.file(file.filename, file.data, { base64: true });
        setProgress(((index + 1) / downloadedFiles.length) * 100);
      }
    });

    setStatus('Generating ZIP file...');

    // Generate the ZIP file
    const zipContent = await zip.generateAsync({ type: 'blob' });

    // Trigger download
    saveAs(zipContent, zipFilename);

    setStatus('Files downloaded successfully'); // Success message for Snackbar
  } catch (error) {
    console.error('Error downloading and zipping files:', error);
    setStatus(`Error: ${error.message}`); // Error message for Snackbar
  } finally {
    setIsDownloading(false);
    setProgress(0);
  }
};
 


// New function for downloading a single file as a Blob (for online viewing, e.g., passing to Three.js loader)
// Returns the Blob on success, or null on failure. You can then create a blob URL with URL.createObjectURL(blob)
// and load it into your Three.js viewer (e.g., via GLTFLoader.load(url, ...)).
export const downloadSingleFile = async (fileLink, setIsDownloading, setStatus) => {
  if (!fileLink) {
    setStatus('No file link provided');
    return null;
  }

  setIsDownloading(true);
  setStatus('Downloading file...');

  try {
    const filename = fileLink.split('/').pop();
    const file = { fileLink, filename };

    // Reuse the bulk endpoint but with a single file (assuming backend handles it gracefully)
    const response = await apiClient.post('/files/bulk-download-stream', {
      organizationId: parseInt(localStorage.getItem('organization_id')),
      files: [file],
    }, {
      responseType: 'json',
    });

    const { files: downloadedFiles } = response.data;
    if (!downloadedFiles || downloadedFiles.length === 0 || !downloadedFiles[0].data) {
      setStatus('No file was downloaded');
      return null;
    }

    const fileData = downloadedFiles[0];

    // Convert base64 data to Blob
    const byteCharacters = atob(fileData.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteNumbers.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { 
      type: fileData.contentType || 'application/octet-stream' // Use contentType if available from backend; fallback otherwise
    });

    setStatus('File downloaded successfully');
    return blob; // Return Blob for use in viewer (e.g., URL.createObjectURL(blob))
  } catch (error) {
    console.error('Error downloading single file:', error);
    setStatus(`Error: ${error.message}`);
    return null;
  } finally {
    setIsDownloading(false);
  }
};